import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { accountSettingsKeys } from '@/hooks/useAccountSettings'
import { useMaskedPasswordField } from '@/hooks/useMaskedPasswordField'
import { useAccountRestoration } from '@/hooks/useAccountRestoration'
import type { AccountDeletionScope } from '@/hooks/useAccountDeletion'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { api } from '@/lib/api'
import {
  clearDeletionNotice,
  formatDeletionDate,
  readDeletionNotice,
  type DeletionDateDisplay,
} from '@/lib/deletionNotice'
import type { ApiResponse, AccountSettings } from '@/types'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { LoginBrandPanel } from '@/components/auth/LoginBrandPanel'

// The body POST /oauth/token answers with while a deletion is pending and the
// password is right (the design doc's own wording): the ordinary invalid_grant
// shape plus a `deletion` object. A wrong password gets the ordinary
// invalid_grant with no `deletion` key, so the account's state is never shown
// to someone who cannot sign in to it.
interface PendingDeletionInfo {
  scope: AccountDeletionScope
  scheduled_at: string
  can_restore: boolean
  display?: DeletionDateDisplay
}

function pendingDeletionFrom(err: unknown): PendingDeletionInfo | null {
  const data = (
    err as { response?: { data?: { error_description?: string; deletion?: PendingDeletionInfo } } }
  ).response?.data
  if (data?.error_description === 'pending_deletion' && data.deletion) return data.deletion
  return null
}

export function LoginPage() {
  const { t } = useTranslation('auth')
  // Uncontrolled inputs (read via refs on submit) instead of value/onChange
  // state. A controlled secure (`type=password`) field re-renders on every
  // keystroke, which resets the iOS WKWebView cursor and silently drops
  // characters under programmatic e2e input (the password collapsed to a
  // few chars → invalid_grant). Uncontrolled inputs let the WebView own the
  // value; React never touches it after mount. Real users + Playwright (which
  // sets the DOM value) are unaffected.
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { isNativeShell, showPassword, toggle: togglePassword, type: passwordType, style: passwordStyle } =
    useMaskedPasswordField()

  // EP-150: the panel replacing the ordinary error message when the account
  // (or this login) is pending deletion, and its own Restore state.
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletionInfo | null>(null)
  const [restoreError, setRestoreError] = useState('')
  const [isRestoring, setIsRestoring] = useState(false)
  // isRestoring disables the button from the next render only.
  const restoringRef = useRef(false)
  const restoreAccount = useAccountRestoration()
  const translateAuthError = useApiErrorTranslator('auth')

  // Left by AccountDeletePage after a deletion request, for the person who
  // has just deleted. It goes at the first sign-in attempt, whoever makes it.
  const [deletionNotice, setDeletionNotice] = useState(readDeletionNotice)

  // EP-80: on the native shell, nudge the focused field into view once the
  // soft keyboard has animated in. The WebView resize (@capacitor/keyboard
  // resize:Native + android:windowSoftInputMode=adjustResize) re-centers the
  // card on its own; this is a belt-and-suspenders so the active field stays
  // visible even if a launcher/IME doesn't resize cleanly. No-op on web.
  const handleFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isNativeShell) return
    const el = e.currentTarget
    setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
  }

  const { login } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Shared by a normal sign-in and a successful Restore-then-sign-in, so the
  // landing decision cannot fork between the two paths.
  const redirectAfterLogin = async (loggedInUser: Awaited<ReturnType<typeof login>>) => {
    // Invalidate any stale pre-login cache, then fetch fresh settings.
    // This ensures a user who completed onboarding in another tab is NOT
    // redirected to the wizard due to a stale cached value.
    await queryClient.invalidateQueries({ queryKey: accountSettingsKeys.detail() })
    const settings = await queryClient.fetchQuery<AccountSettings>({
      queryKey: accountSettingsKeys.detail(),
      queryFn: async () => {
        const { data } = await api.get<ApiResponse<AccountSettings>>('/api/account_settings')
        return data.data
      },
    })

    // Only force the wizard for truly-fresh accounts. If properties_count > 0
    // the workspace has real data, so respect it by landing on the dashboard
    // (which still surfaces the resume-setup checklist). Off + Mint flagged
    // that landing in the wizard despite an already-populated workspace reads
    // as a UX contradiction.
    //
    // Use `!== true` (not `=== false`) so accounts with an empty settings
    // JSONB, including brand-new accounts created via the super-admin
    // POST /api/admin/accounts tool, which never seed `onboarding_completed`,
    // still fall into the wizard path on first login.
    // Onboarding is an OWNER-only flow (create-property, seed sample data, and
    // the wizard all require owner/admin authz; the seed endpoints are
    // admin_or_higher, so a staff/non-owner sent here would hit a 403). Only
    // the owner is routed into onboarding; everyone else lands on the dashboard.
    const hasProperties = (settings?.properties_count ?? 0) > 0
    const isOwner = loggedInUser?.role === 'owner'
    if (isOwner && settings?.settings?.onboarding_completed !== true && !hasProperties) {
      const s = settings?.settings
      // T005 (EP-86): a fresh account (no resume marker) goes to the
      // mode-chooser at /onboarding (no ?step). An account that was
      // already mid-wizard resumes at /onboarding?step=N with its
      // saved entity IDs so the prerequisite guards land correctly.
      const hasResumeMarker = Boolean(
        s?.onboarding_step ||
        s?.onboarding_property_id ||
        s?.onboarding_customer_id ||
        s?.onboarding_lease_id ||
        s?.onboarding_invoice_id ||
        s?.onboarding_data_import_id,
      )
      if (hasResumeMarker) {
        const params = new URLSearchParams({ step: String(s?.onboarding_step ?? 1) })
        if (s?.onboarding_property_id) params.set('property', String(s.onboarding_property_id))
        if (s?.onboarding_customer_id) params.set('customer', String(s.onboarding_customer_id))
        if (s?.onboarding_lease_id) params.set('lease', String(s.onboarding_lease_id))
        if (s?.onboarding_invoice_id) params.set('invoice', String(s.onboarding_invoice_id))
        if (s?.onboarding_data_import_id) params.set('import', String(s.onboarding_data_import_id))
        navigate(`/onboarding?${params.toString()}`)
      } else {
        // Truly fresh account, show the mode chooser
        navigate('/onboarding')
      }
    } else {
      navigate('/')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setPendingDeletion(null)
    setRestoreError('')
    clearDeletionNotice()
    setDeletionNotice(null)
    setIsSubmitting(true)

    const email = emailRef.current?.value ?? ''
    const password = passwordRef.current?.value ?? ''

    try {
      const loggedInUser = await login(email, password)
      await redirectAfterLogin(loggedInUser)
    } catch (err) {
      const info = pendingDeletionFrom(err)
      if (info) {
        setPendingDeletion(info)
      } else {
        setError(t('errors.invalid_credentials'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // EP-150: restore with the same email/password just typed, then sign in
  // through the normal path and land where a normal sign-in lands.
  const handleRestore = async () => {
    if (restoringRef.current) return
    restoringRef.current = true
    setRestoreError('')
    setIsRestoring(true)

    const email = emailRef.current?.value ?? ''
    const password = passwordRef.current?.value ?? ''

    try {
      await restoreAccount.mutateAsync({ email, password })
      setPendingDeletion(null)
      const loggedInUser = await login(email, password)
      await redirectAfterLogin(loggedInUser)
    } catch (err) {
      setRestoreError(translateAuthError(err))
    } finally {
      setIsRestoring(false)
      restoringRef.current = false
    }
  }

  // The login is a full-screen split: a solid primary-color hero with the
  // marketing headline (left on desktop, top on mobile) and a white form panel
  // (right on desktop, a rounded sheet that rises over the hero on mobile).
  return (
    <div className="min-h-screen bg-primary flex flex-col lg:grid lg:grid-cols-[2fr_1fr]">
      {/* Mobile collage backdrop (iOS Safari edge-to-edge fix). iOS Safari fills
          the top status-bar strip with the ROOT (<html>) canvas color and lays
          page content out BELOW that strip, so a normal hero can't reach the top
          edge. We keep <html> white (bottom Safari toolbar stays white) and render
          the hero's collage+wash ONCE here as a FIXED backdrop pinned to the
          physical top edge, spanning the strip + the whole hero, so the collage
          image bleeds off the very top edge as one continuous image. It needs a
          POSITIVE z-index to paint the status-bar strip (a negative/zero z leaves
          it the white canvas), so the headline (LoginBrandPanel) and the white
          form panel are raised to z-[60] above it: the headline stays legible and
          the form covers the backdrop's lower half, keeping the bottom white.
          lg:hidden — desktop renders the collage inside LoginBrandPanel instead. */}
      <div
        aria-hidden="true"
        className="lg:hidden fixed inset-x-0 top-0 z-50 h-[45vh] overflow-hidden bg-primary pointer-events-none"
      >
        <div className="absolute inset-[-30%] -translate-y-28 rotate-[-10deg] scale-125 bg-[url('/images/login-collage.png')] bg-repeat opacity-60 [background-size:440px_auto]" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/55 to-primary/80" />
      </div>

      {/* Hero — headline over the primary-color backdrop. On mobile it GROWS to
          fill the space the (content-sized) form sheet leaves, so there is no
          dead white space below the form; on desktop the grid cell stretches. */}
      <LoginBrandPanel className="relative z-[60] flex flex-1 min-h-[24vh] pt-[max(env(safe-area-inset-top),1rem)]" />

      {/* Form panel — white. A rounded sheet rising over the hero on mobile,
          sized to its content (no flex-1) so it hugs the fields; the vertically-
          centered right half on desktop (grid cell stretch). */}
      <div className="relative z-[60] -mt-12 lg:mt-0 bg-base-100 rounded-t-3xl lg:rounded-none flex flex-col justify-start lg:justify-center px-6 pt-8 pb-[max(env(safe-area-inset-bottom),2rem)] sm:px-10 lg:px-16">
        <div className="w-full max-w-sm mx-auto">
          <div className="flex items-center justify-start lg:justify-center gap-2 mb-6 lg:mb-14">
            <img src="/icon-clean.svg" alt="" className="w-9 h-9 rounded-lg ring-1 ring-base-300" />
            <span className="font-bold text-lg text-base-content">FlowRent</span>
          </div>

          {/* Welcome heading — mobile only; the desktop Figma (47-1104) omits it. */}
          <div className="mb-6 lg:hidden">
            <h1 className="text-2xl font-bold">{t('login.welcome')}</h1>
            <p className="text-base-content/60">{t('login.subtitle')}</p>
          </div>

          {/* EP-150: informational, not an error. Shown after AccountDeletePage
              signs the person out and sends them here. */}
          {deletionNotice && !pendingDeletion && (
            <Alert variant="info" icon={null} className="mb-4" data-testid="deletion-scheduled-notice">
              <span>
                {t(
                  deletionNotice.scope === 'account'
                    ? 'login.deletion_notice.account'
                    : 'login.deletion_notice.user',
                  { date: formatDeletionDate(deletionNotice.scheduledAt, deletionNotice.display) },
                )}
              </span>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
            {pendingDeletion ? (
              // EP-150: replaces the ordinary wrong-password message, since the
              // account/login is pending deletion, not simply unreachable.
              <Alert variant="warning" icon={null} data-testid="pending-deletion-panel">
                <div className="space-y-3">
                  <p>
                    {t(
                      pendingDeletion.scope === 'account'
                        ? 'login.pending_deletion.account_message'
                        : 'login.pending_deletion.user_message',
                      { date: formatDeletionDate(pendingDeletion.scheduled_at, pendingDeletion.display) },
                    )}
                  </p>
                  {pendingDeletion.can_restore ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      loading={isRestoring}
                      disabled={isRestoring}
                      onClick={handleRestore}
                      data-testid="restore-account-button"
                    >
                      {t('login.pending_deletion.restore_button')}
                    </Button>
                  ) : (
                    <p className="text-sm">{t('login.pending_deletion.owner_only')}</p>
                  )}
                </div>
              </Alert>
            ) : (
              error && (
                <Alert variant="error" icon={null}>
                  <span>{error}</span>
                </Alert>
              )
            )}

            {/* Its own alert, below the panel: red text on the panel's amber
                could not be read. */}
            {pendingDeletion && restoreError && (
              <Alert variant="error" icon={null} data-testid="restore-error">
                <span>{restoreError}</span>
              </Alert>
            )}

            <Input
              id="login-email"
              data-testid="login-email"
              label={t('login.email')}
              type="email"
              name="username"
              autoComplete="username"
              placeholder={t('login.email_placeholder')}
              onFocus={handleFieldFocus}
              ref={emailRef}
              defaultValue=""
              required
            />

            <div className="relative">
              <Input
                id="login-password"
                data-testid="login-password"
                label={t('login.password')}
                type={passwordType}
                name="password"
                autoComplete="current-password"
                placeholder={t('login.password_placeholder')}
                inputClassName="pr-10"
                style={passwordStyle}
                onFocus={handleFieldFocus}
                ref={passwordRef}
                defaultValue=""
                required
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={togglePassword}
                aria-label={t('login.toggle_password_visibility')}
                className="absolute right-3 bottom-0 h-10 flex items-center text-base-content/50 hover:text-base-content"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>

            <div className="flex justify-between">
              {/* EP-144 Q-4: the EP-126 external "Request a trial" anchor became
                  the internal sign-up route. The marketing lead form survives on
                  the sign-up page as its "No invitation code? Contact us" link. */}
              <Link to="/signup" className="link link-hover text-sm text-primary">
                {t('login.signup_link')}
              </Link>
              <a
                href="https://flowrent.io/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="link link-hover text-sm text-base-content/60"
              >
                {t('login.privacy_policy')}
              </a>
              <Link to="/forgot-password" className="link link-hover text-sm text-primary">
                {t('login.forgot_password')}
              </Link>
            </div>
            </div>

            {/* Section gap before the primary action — 56px desktop / 24px mobile (Figma). */}
            <Button
              type="submit"
              id="login-submit"
              data-testid="login-submit"
              block
              className="mt-6 lg:mt-14"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t('login.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
